import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Badge, StatusDot } from '@wit-support/ui';
import { cn } from './lib/cn.js';
import { capitalize } from './lib/capitalize.js';
import { STATUS_LABEL_RU } from './status-labels.js';
import { RISK_ACCOUNT_STATUS_RU } from './risk-labels.js';
const CHANNEL_MAP = {
    active: { dot: 'green', label: 'Active' },
    paused: { dot: 'slate', label: 'Paused' },
    reauth_required: { dot: 'amber', label: 'Reauth required' },
    error: { dot: 'red', label: 'Error' },
    pending_oauth: { dot: 'blue', label: 'Pending OAuth' },
};
/**
 * OBSERVED health overrides the operator-intent status in the pill.
 *
 * The whole point of D1: `status` stays `active` (the supervisor must keep
 * polling a broken channel so it can heal itself), but the operator must see
 * red rather than a green «Active» — that green pill is exactly what hid a
 * two-week outage. Labels are in Russian and say what to DO, not what the
 * internal state is called.
 */
const HEALTH_MAP = {
    auth_failed: { dot: 'red', label: 'Требуется переподключение' },
    failing: { dot: 'red', label: 'Сбой канала' },
    stalled: { dot: 'amber', label: 'Опрос не идёт' },
    silent: { dot: 'amber', label: 'Нет входящих' },
};
const CONVERSATION_MAP = {
    open: { dot: 'blue', label: STATUS_LABEL_RU.open },
    pending: { dot: 'amber', label: STATUS_LABEL_RU.pending },
    waiting: { dot: 'amber', label: STATUS_LABEL_RU.waiting },
    resolved: { dot: 'green', label: STATUS_LABEL_RU.resolved },
    snoozed: { dot: 'slate', label: STATUS_LABEL_RU.snoozed },
    closed: { dot: 'slate', label: STATUS_LABEL_RU.closed },
};
const ADMIN_MAP = {
    active: { dot: 'green', label: 'Active' },
    disabled: { dot: 'slate', label: 'Disabled' },
};
/**
 * Аккаунт источника риска — тот же жизненный цикл, что у канала почты, и та же
 * пилюля: `active` значит «опрашивается», `disabled` — «заведён, но молчит».
 * Отдельный `kind`, а не `admin`: union совпадает случайно, а подписи у
 * риск-домена русские и живут в его собственном словаре.
 */
const RISK_ACCOUNT_MAP = {
    active: { dot: 'green', label: RISK_ACCOUNT_STATUS_RU.active },
    disabled: { dot: 'slate', label: RISK_ACCOUNT_STATUS_RU.disabled },
};
/**
 * Discriminated pill: the `kind` decides which status enum + label mapping
 * to use. Structure reuses the ui-kit `Badge` (variant neutral) and
 * `StatusDot` primitives so both keep token-only styling and dark-mode
 * behaviour.
 */
export const StatusPill = React.forwardRef((props, ref) => {
    const { kind, size = 'md', className } = props;
    const entry = kind === 'channel'
        ? props.health && props.health !== 'ok'
            ? HEALTH_MAP[props.health]
            : CHANNEL_MAP[props.status]
        : kind === 'admin'
            ? ADMIN_MAP[props.status]
            : kind === 'risk_account'
                ? RISK_ACCOUNT_MAP[props.status]
                : CONVERSATION_MAP[props.status];
    // Exhaustiveness fallback — if the union ever grows, fall back to the raw
    // status string rather than rendering nothing.
    const label = entry?.label ?? capitalize(String(props.status));
    const dot = entry?.dot ?? 'slate';
    return (_jsxs(Badge, { ref: ref, size: size, variant: "neutral", className: cn(className), children: [_jsx(StatusDot, { variant: dot, "aria-hidden": "true" }), _jsx("span", { children: label })] }));
});
StatusPill.displayName = 'StatusPill';
//# sourceMappingURL=status-pill.js.map